<template>
  <component
    :is="tag"
    class="dropdown"
    ref="dropdownref"
    :class="[{ show: isOpen }, `drop${direction}`]"
    @click="toggleDropDown"
    v-click-outside="closeDropDown"
    autofocus
  >
    <slot name="title-container" :is-open="isOpen">
      <component
        :is="titleTag"
        class="dropdown-toggle no-caret"
        :class="titleClasses"
        :aria-label="title || 'dropdown'"
        :aria-expanded="isOpen"
        data-toggle="dropdown"
      >
        <slot name="title" :is-open="isOpen">
          <i :class="icon"></i> {{ title }}
        </slot>
      </component>
    </slot>
    <ul
      ref="menu"
      style="max-width: 90vw;"
      class="dropdown-menu"
      :class="[
        { show: isOpen && (!appendToBody || isMobile) },
        { 'dropdown-menu-right': menuOnRight && !appendToBody },
        { 'bdd-fullscreen': appendToBody && isMobile && isOpen },
        { 'bdd-popper-menu': popperActive },
        { 'bdd-visible': popperVisible },
        menuClasses
      ]"
    >
      <!-- Flecha/triángulo para modo desktop con appendToBody -->
      <div v-if="appendToBody && !isMobile" class="bdd-arrow" x-arrow></div>
      <!-- Header para cerrar en modo fullscreen móvil -->
      <li v-if="appendToBody && isMobile && isOpen" class="bdd-fs-header">
        <span class="bdd-fs-title">
          <slot name="fullscreen-title">{{ title || 'Opciones' }}</slot>
        </span>
        <button type="button" class="bdd-fs-close" @click.stop="closeDropDown" aria-label="Cerrar">
          <i class="fas fa-times"></i>
        </button>
      </li>
      <slot></slot>
    </ul>
  </component>
</template>
<script>
import Popper from 'popper.js';

const MOBILE_BREAKPOINT = 576;

export default {
  name: 'base-dropdown',
  props: {
    tag: {
      type: String,
      default: 'div',
      description: 'Dropdown html tag (e.g div, ul etc)'
    },
    titleTag: {
      type: String,
      default: 'button',
      description: 'Dropdown title (toggle) html tag'
    },
    title: {
      type: String,
      description: 'Dropdown title'
    },
    direction: {
      type: String,
      default: 'down', // up | down
      description: 'Dropdown menu direction (up|down)'
    },
    icon: {
      type: String,
      description: 'Dropdown icon'
    },
    titleClasses: {
      type: [String, Object, Array],
      description: 'Title css classes'
    },
    menuClasses: {
      type: [String, Object],
      description: 'Menu css classes'
    },
    menuOnRight: {
      type: Boolean,
      description: 'Whether menu should appear on the right'
    },
    appendToBody: {
      type: Boolean,
      default: false,
      description: 'Whether to append menu to body to escape overflow hidden containers'
    }
  },
  data() {
    return {
      isOpen: false,
      popperInstance: null,
      isMobile: false,
      popperActive: false,
      popperVisible: false,
      _backdrop: null,
      _closeTimer: null
    };
  },
  watch: {
    isOpen(val) {
      if (!this.appendToBody) return;

      // Cancelar animación de cierre pendiente
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }

      if (val) {
        this.$nextTick(() => {
          if (this.isMobile) {
            this._openFullscreen();
          } else {
            this._openPopper();
          }
        });
      } else {
        if (this.isMobile) {
          this._closeFullscreen();
        } else if (this.popperActive) {
          this._closePopper();
        }
      }
    }
  },
  mounted() {
    if (this.appendToBody) {
      this._checkMobile();
      this._onResize = () => {
        const wasMobile = this.isMobile;
        this._checkMobile();
        if (this.isOpen && wasMobile !== this.isMobile) {
          this._closeFullscreen();
          this.popperVisible = false;
          this._destroyPopper();
          this.popperActive = false;
          this.$nextTick(() => {
            if (this.isMobile) {
              this._openFullscreen();
            } else {
              this._openPopper();
            }
          });
        }
      };
      window.addEventListener('resize', this._onResize, { passive: true });
    }
  },
  methods: {
    toggleDropDown() {
      this.isOpen = !this.isOpen;
      this.$emit('change', this.isOpen);
      if (this.isOpen) {
        setTimeout(() => {
          this.$refs.dropdownref.focus();
        }, 100);
      }
    },
    closeDropDown() {
      this.isOpen = false;
      this.$emit('change', false);
    },
    _checkMobile() {
      this.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    },

    /* ── Modo fullscreen (móvil) ── */
    _openFullscreen() {
      const menu = this.$refs.menu;
      if (!menu) return;

      this._backdrop = document.createElement('div');
      this._backdrop.className = 'bdd-backdrop';
      this._backdrop.addEventListener('click', () => this.closeDropDown());
      document.body.appendChild(this._backdrop);

      document.body.appendChild(menu);
      document.body.classList.add('bdd-body-locked');
    },
    _closeFullscreen() {
      if (this._backdrop && this._backdrop.parentNode) {
        this._backdrop.parentNode.removeChild(this._backdrop);
        this._backdrop = null;
      }

      const menu = this.$refs.menu;
      if (menu && menu.parentNode === document.body) {
        this.$refs.dropdownref.appendChild(menu);
      }

      document.body.classList.remove('bdd-body-locked');
    },

    /* ── Modo Popper (desktop) ── */
    _openPopper() {
      const menu = this.$refs.menu;
      const toggle = this.$refs.dropdownref;
      if (!menu || !toggle) return;

      // Toggle rápido: si ya está activo, solo re-posicionar y revelar
      if (this.popperActive && this.popperInstance) {
        this.popperInstance.scheduleUpdate();
        requestAnimationFrame(() => { this.popperVisible = true; });
        return;
      }

      // Evitar flash de scroll: posicionar en 0,0 oculto antes de mover al body
      menu.style.top = '0';
      menu.style.left = '0';
      document.body.appendChild(menu);
      this.popperActive = true;

      const placement = this.direction === 'up'
        ? (this.menuOnRight ? 'top-end' : 'top-start')
        : (this.menuOnRight ? 'bottom-end' : 'bottom-start');

      this.popperInstance = new Popper(toggle, menu, {
        placement: placement,
        modifiers: {
          computeStyle: { gpuAcceleration: false },
          preventOverflow: {
            boundariesElement: 'viewport',
            escapeWithReference: false
          },
          flip: { enabled: true },
          arrow: {
            element: '.bdd-arrow',
            enabled: true
          },
          offset: {
            offset: '0, 8'
          }
        },
        onCreate: () => {
          // Popper posicionó el menú; revelar con animación
          requestAnimationFrame(() => {
            this.popperVisible = true;
          });
        }
      });
    },

    /* ── Cerrar Popper con animación ── */
    _closePopper() {
      this.popperVisible = false;
      this._closeTimer = setTimeout(() => {
        this._destroyPopper();
        this.popperActive = false;
        this._closeTimer = null;
      }, 150);
    },

    _destroyPopper() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
      const menu = this.$refs.menu;
      if (menu && menu.parentNode === document.body) {
        menu.style.top = '';
        menu.style.left = '';
        menu.style.position = '';
        menu.style.willChange = '';
        this.$refs.dropdownref.appendChild(menu);
      }
    }
  },
  beforeDestroy() {
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
    }
    this._closeFullscreen();
    this.popperVisible = false;
    this.popperActive = false;
    this._destroyPopper();
    const menu = this.$refs.menu;
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu);
    }
  }
};
</script>
<style lang="scss" scoped>
.dropdown {
  cursor: pointer;
  user-select: none;
}
</style>

<!-- Estilos globales para el modo fullscreen y popper del dropdown -->
<style>
/* Bloquear scroll del body cuando el fullscreen está abierto */
.bdd-body-locked {
  overflow: hidden !important;
}

/* Backdrop semi-transparente */
.bdd-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1049;
}

/* ── Menú desktop con Popper ── */
.bdd-popper-menu {
  display: block !important;
  z-index: 1050 !important;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin: 0 !important;
  overflow: visible !important;
  /* Animación de entrada/salida */
  opacity: 0;
  transform: scale(0.96);
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.bdd-popper-menu.bdd-visible {
  opacity: 1;
  transform: scale(1);
  pointer-events: auto;
}

/* Transform-origin según la posición del menú */
.bdd-popper-menu[x-placement="bottom-end"]  { transform-origin: top right; }
.bdd-popper-menu[x-placement="bottom-start"]{ transform-origin: top left; }
.bdd-popper-menu[x-placement="top-end"]     { transform-origin: bottom right; }
.bdd-popper-menu[x-placement="top-start"]   { transform-origin: bottom left; }

/* Ocultar los triángulos del tema (Black Dashboard) en el menú popper;
   usamos nuestro propio .bdd-arrow en su lugar */
.bdd-popper-menu::before,
.bdd-popper-menu::after {
  display: none !important;
}

/* Flecha / triángulo — Popper posiciona el left automáticamente */
.bdd-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
}

/* Un solo pseudo-elemento: cuadrado rotado con borde en 2 lados */
.bdd-arrow::before {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
}

/* ── Placement bottom-* : flecha arriba del menú apuntando hacia arriba ── */
.bdd-popper-menu[x-placement^="bottom"] .bdd-arrow {
  top: -6px;
}

/* Solo borde superior e izquierdo (apuntan hacia arriba al rotar 45°) */
.bdd-popper-menu[x-placement^="bottom"] .bdd-arrow::before {
  border-top: 1px solid rgba(0, 0, 0, 0.15);
  border-left: 1px solid rgba(0, 0, 0, 0.15);
}

/* ── Placement top-* : flecha debajo del menú apuntando hacia abajo ── */
.bdd-popper-menu[x-placement^="top"] .bdd-arrow {
  bottom: -6px;
}

/* Solo borde inferior y derecho (apuntan hacia abajo al rotar 45°) */
.bdd-popper-menu[x-placement^="top"] .bdd-arrow::before {
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  border-right: 1px solid rgba(0, 0, 0, 0.15);
}

/* Menú en modo fullscreen (móvil) */
.bdd-fullscreen {
  position: fixed !important;
  inset: 0 !important;
  transform: none !important;
  max-width: 100vw !important;
  width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  z-index: 1050 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 !important;
  animation: bddSlideUp 0.25s ease;
}

/* Header del fullscreen */
.bdd-fs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  background: inherit;
  z-index: 1;
  list-style: none;
}

.bdd-fs-title {
  font-size: 1rem;
  font-weight: 600;
  color: #344767;
}

.bdd-fs-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #8898aa;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
  transition: color 0.15s, background 0.15s;
}

.bdd-fs-close:hover {
  color: #f5365c;
  background: rgba(245, 54, 92, 0.08);
}

@keyframes bddSlideUp {
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
